const PADDING = 26;
const OVERFLOW_REGEX = /(auto|scroll)/;

const inRange = (minus: number, plus: number) => (
	number: number,
	base: number
) => number >= base + minus && number <= base + plus;
const within4 = inRange(-2, 2);

// adapted from react-select, which seems to come from jQuery
function getScrollParent(element: HTMLElement) {
	const style = window.getComputedStyle(element);
	const excludeStaticParent = style.position === 'absolute';

	if (style.position === 'fixed') {
		return document.documentElement;
	} else {
		return getParentElement(excludeStaticParent)(element.parentElement);
	}
}

function getParentElement(excludeStaticParent: boolean) {
	return function looper(el: HTMLElement | null): HTMLElement {
		if (el === null || el === document.documentElement) {
			return document.documentElement;
		} else {
			const style = window.getComputedStyle(el);
			if (
				OVERFLOW_REGEX.test(
					// only via command line does TS complain about style.overflow and
					// style.overflowY possibly being null... yet style.overflowX is fine
					// not sure if it's TS version thing or whatever but adding these checks...
					(style.overflow || '') + (style.overflowY || '') + style.overflowX
				) &&
				!(excludeStaticParent && style.position === 'static')
			) {
				return el;
			} else {
				return looper(el.parentElement);
			}
		}
	};
}

let lastTarget: HTMLElement | null = null;
export default function focusinHandler(e: FocusEvent) {
	const target = e.target as HTMLElement | null;

	// PRO-9952 prevent re-focusing the same element; can happen while alt-tabbing to copy/paste data entry.
	if (lastTarget === target) {
		return;
	}
	lastTarget = target;

	if (target && target.getAttribute('tabindex') !== '-1') {
		const scrollParent = getScrollParent(target);
		const stickies = Array.from<HTMLElement>(
			scrollParent.querySelectorAll('[data-sticky]')
		)
			.filter((el) => scrollParent === getScrollParent(el))
			.filter((el) => {
				const elTop = Math.round(el.getBoundingClientRect().top);
				const elStyleTop = Math.round(
					parseFloat(window.getComputedStyle(el).top || '0')
				);
				if (scrollParent === document.documentElement) {
					// if scrollParent is the document, we have simpler calculations
					return within4(elTop, elStyleTop);
				} else {
					// in a modal or something, or a scroll within a scroll
					const scrollParentTop =
						Math.round(scrollParent.getBoundingClientRect().top) +
						Math.round(
							parseFloat(
								window.getComputedStyle(scrollParent).borderTopWidth || '0'
							)
						);

					return within4(elTop - scrollParentTop, elStyleTop);
				}
			});

		if (
			stickies.length &&
			stickies.every((sticky) => !sticky.contains(target))
		) {
			const stickyBottom =
				stickies.reduce(
					(acc, next) => Math.max(acc, next.getBoundingClientRect().bottom),
					0
				) + PADDING;
			const rect = target.getBoundingClientRect();
			const delta = rect.top - Math.ceil(stickyBottom);

			if (
				Math.abs(delta) !== delta &&
				// if enough of the target is sticking below the sticky elements, don't
				// do anything
				rect.bottom < stickyBottom * 3
			) {
				// Safari can't do scrollBy on document, but can on window
				const scroller =
					scrollParent === document.documentElement ? window : scrollParent;
				let scrollY;
				// if the target is taller than the viewport, scroll not to the top of
				// the target, but just enough to bring the bottom of it to the bottom
				// of the viewport
				if (rect.height > document.documentElement.clientHeight) {
					scrollY = -(document.documentElement.clientHeight - rect.bottom);
				} else {
					scrollY = delta - PADDING;
				}

				if (typeof scroller.scrollBy === 'function') {
					// element.scrollBy isn't supported in IE11 and Edge...
					scroller.scrollBy(window.pageXOffset, scrollY);
				} else if (scroller !== window) {
					// if scrollBy isn't supported and it's not window/document, we can use
					// scrollTop adjustment
					// hat tip: https://stackoverflow.com/a/29012684
					(scroller as HTMLElement).scrollTop += scrollY;
				}
			}
		}
	}
}
